/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Tabs, Tab } from 'carbon-components-react';
import React, { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useInterval } from 'react-use';

import { useAppDispatch, useAppSelector } from '../../../store';
import {
  ELASTIC_SEARCH_INDEX_INPUT_METHODS,
  SearchSettingsElastic
} from '../../../ts/skill';
import { SearchResultDialogs } from '../../discovery/components/search-detail-config/search-result-dialogs/SearchResultDialogs';
import { listDocs } from '../store/actions';
import {
  selectElasticIndexInputMethod,
  selectFiles,
  selectIsDocumentUploadEnabled
} from '../store/selectors';
import { ConfigureContent } from './ConfigureContent';
import { ElasticSearchInstanceConfig } from './elastic-search-instance-config/ElasticSearchInstanceConfig';
import { ElasticSearchDocuments } from './ElasticSearchDocuments';
import { ElasticSearchIndexConfig } from './ElasticSearchIndexConfig';
import { messages } from './messages';
import { UploadPanel } from './UploadPanel';

import './ElasticSearchTabs.scss';

interface ElasticSearchTabsProps {
  assistantName: string;
  elasticState: SearchSettingsElastic;
  draft: {
    noResultsMessage: string;
    setNoResultsMessage: () => void;
    connectivityMessage: string;
    setConnectivityMessage: () => void;
  };
  skillId: string;
  assistantId: string;
  readOnly: boolean;
  onClose: () => void;
}

export const ElasticSearchTabs = ({
  assistantName,
  elasticState,
  draft,
  skillId,
  assistantId,
  readOnly,
  onClose
}: ElasticSearchTabsProps) => {
  const dispatch = useAppDispatch();
  const DEFAULT_INTERVAL = 3000;
  const { formatMessage: t } = useIntl();
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const inputMethod = useAppSelector(selectElasticIndexInputMethod);
  const [tabIndex, setTabIndex] = useState(
    inputMethod === ELASTIC_SEARCH_INDEX_INPUT_METHODS.INDEX ? 1 : 0
  );
  const files = useAppSelector(selectFiles);
  const [fileCount, setFileCount] = useState(files.length);
  const isDocumentUploadEnabled = useAppSelector(selectIsDocumentUploadEnabled);

  const showPanel = () => {
    setShowUploadPanel(true);
  };
  const closePanel = () => {
    setShowUploadPanel(false);
  };

  const handleTabChange = selectedIndex => {
    setTabIndex(selectedIndex);
    closePanel();
  };

  const addedFiles = num => {
    setFileCount(files.length + num);
  };

  const deletedFiles = num => {
    setFileCount(fileCount - num);
  };

  const checkFileStatus = useCallback(() => {
    dispatch(listDocs({ skillId, assistantId, noLoad: true }));
  }, [dispatch, skillId, assistantId]);

  useInterval(
    checkFileStatus,
    files.length < fileCount ? DEFAULT_INTERVAL : null
  );

  return (
    <div className="ElasticSearch">
      <div className="ElasticSearch__container">
        <Tabs
          type="container"
          light
          // tabIndex={tabIndex}
          selected={tabIndex}
          onSelectionChange={handleTabChange}>
          {isDocumentUploadEnabled && (
            <Tab
              id="ElasticSearchTabs-documents"
              label={t(messages.documents)}
              disabled={
                !elasticState.hasOwnProperty('managed_index') ||
                elasticState.index !== elasticState.managed_index
              }>
              <ElasticSearchDocuments
                assistantId={assistantId}
                showPanel={showPanel}
                deletedFiles={deletedFiles}
                uploadPanel={showUploadPanel}
                skillId={skillId}
                readOnly={readOnly}
              />
            </Tab>
          )}
          <Tab id="ElasticSearchTabs-settings" label={t(messages.settings)}>
            <div className="ElasticSearch__settings">
              <div className="ElasticSearch__settings-container">
                <ElasticSearchIndexConfig
                  tabDisplay={true}
                  assistantName={assistantName}
                  readOnly={readOnly}
                />
                <ConfigureContent tabDisplay={true} readOnly={readOnly} />
                <SearchResultDialogs
                  hideSuccessMessageTab={true}
                  noResultsMessage={draft.noResultsMessage}
                  setNoResultsMessage={draft.setNoResultsMessage}
                  connectivityMessage={draft.connectivityMessage}
                  setConnectivityMessage={draft.setConnectivityMessage}
                />
              </div>
            </div>
          </Tab>
          <Tab
            className="ElasticSearchTabs-tab"
            id="ElasticSearchTabs-instanced"
            label={t(messages.instance)}>
            <ElasticSearchInstanceConfig
              noHeader={true}
              readOnly={readOnly}
              onClose={onClose}
            />
          </Tab>
        </Tabs>
      </div>
      {showUploadPanel && (
        <UploadPanel
          addedFiles={addedFiles}
          closePanel={closePanel}
          skillId={skillId}
          assistantId={assistantId}
        />
      )}
    </div>
  );
};
